import { createChatThreadPath } from '@affine/core/blocksuite/ai/chat-panel/navigation';
import type { SettingTab } from '@affine/core/modules/dialogs/constant';
import { toDocSearchParams } from '@affine/core/modules/navigation';
import { getOpenUrlInDesktopAppLink } from '@affine/core/modules/open-in-app';
import {
  useCollectionsFeatureEnabled,
  useTagsFeatureEnabled,
} from '@affine/core/modules/streamlain/hooks/use-product-feature-gates';
import type { DocMode } from '@blocksuite/affine/model';
import { nanoid } from 'nanoid';
import { createContext, useCallback, useContext, useMemo } from 'react';
import type { NavigateFunction, NavigateOptions } from 'react-router-dom';

/**
 * In workbench, we use nested react-router, so default `useNavigate` can't get correct navigate function in workbench.
 * We use this context to provide navigate function for whole app.
 */
export const NavigateContext = createContext<NavigateFunction | null>(null);

export enum RouteLogic {
  REPLACE = 'replace',
  PUSH = 'push',
}

// TODO(@eyhn): add a name -> path helper in the results
/**
 * Use this for over workbench navigate, for navigate in workbench, use `WorkbenchService`.
 */
export function useNavigateHelper() {
  const navigate = useContext(NavigateContext);
  const collectionsEnabled = useCollectionsFeatureEnabled();
  const tagsEnabled = useTagsFeatureEnabled();

  if (!navigate) {
    throw new Error('useNavigateHelper must be used within a NavigateProvider');
  }

  const jumpToPage = useCallback(
    (
      workspaceId: string,
      pageId: string,
      logic: RouteLogic = RouteLogic.PUSH
    ) => {
      void navigate(`/workspace/${workspaceId}/${pageId}`, {
        replace: logic === RouteLogic.REPLACE,
      });
    },
    [navigate]
  );
  const jumpToPageBlock = useCallback(
    (
      workspaceId: string,
      pageId: string,
      mode?: DocMode,
      blockIds?: string[],
      elementIds?: string[],
      logic: RouteLogic = RouteLogic.PUSH
    ) => {
      const search = toDocSearchParams({
        mode,
        blockIds,
        elementIds,
        refreshKey: nanoid(),
      });
      const query = search?.size ? `?${search.toString()}` : '';
      void navigate(`/workspace/${workspaceId}/${pageId}${query}`, {
        replace: logic === RouteLogic.REPLACE,
      });
    },
    [navigate]
  );
  const jumpToPageComment = useCallback(
    (
      workspaceId: string,
      pageId: string,
      commentId: string,
      mode: DocMode,
      logic: RouteLogic = RouteLogic.PUSH
    ) => {
      const search = toDocSearchParams({
        mode,
        refreshKey: nanoid(),
        commentId,
      });
      const query = search?.size ? `?${search.toString()}` : '';
      void navigate(`/workspace/${workspaceId}/${pageId}${query}`, {
        replace: logic === RouteLogic.REPLACE,
      });
    },
    [navigate]
  );
  const jumpToCollections = useCallback(
    (workspaceId: string, logic: RouteLogic = RouteLogic.PUSH) => {
      void navigate(
        `/workspace/${workspaceId}/${collectionsEnabled ? 'collection' : 'all'}`,
        {
          replace: logic === RouteLogic.REPLACE,
        }
      );
    },
    [collectionsEnabled, navigate]
  );
  const jumpToAll = useCallback(
    (workspaceId: string, logic: RouteLogic = RouteLogic.PUSH) => {
      void navigate(`/workspace/${workspaceId}/all`, {
        replace: logic === RouteLogic.REPLACE,
      });
    },
    [navigate]
  );
  const jumpToTags = useCallback(
    (workspaceId: string, logic: RouteLogic = RouteLogic.PUSH) => {
      void navigate(
        `/workspace/${workspaceId}/${tagsEnabled ? 'tag' : 'all'}`,
        {
          replace: logic === RouteLogic.REPLACE,
        }
      );
    },
    [navigate, tagsEnabled]
  );
  const jumpToTag = useCallback(
    (
      workspaceId: string,
      tagId: string,
      logic: RouteLogic = RouteLogic.PUSH
    ) => {
      void navigate(
        `/workspace/${workspaceId}/${tagsEnabled ? `tag/${tagId}` : 'all'}`,
        {
          replace: logic === RouteLogic.REPLACE,
        }
      );
    },
    [navigate, tagsEnabled]
  );
  const jumpToCollection = useCallback(
    (
      workspaceId: string,
      collectionId: string,
      logic: RouteLogic = RouteLogic.PUSH
    ) => {
      void navigate(
        `/workspace/${workspaceId}/${
          collectionsEnabled ? `collection/${collectionId}` : 'all'
        }`,
        {
          replace: logic === RouteLogic.REPLACE,
        }
      );
    },
    [collectionsEnabled, navigate]
  );

  const openPage = useCallback(
    (workspaceId: string, pageId: string, logic?: RouteLogic) => {
      return jumpToPage(workspaceId, pageId, logic);
    },
    [jumpToPage]
  );

  const jumpToWorkflows = useCallback(
    (
      workspaceId: string,
      opt?: { id?: string | null; name?: string | null },
      logic: RouteLogic = RouteLogic.PUSH
    ) => {
      const searchParams = new URLSearchParams();
      if (opt?.id) searchParams.set('id', opt.id);
      if (opt?.name) searchParams.set('name', opt.name);
      const query = searchParams.size ? `?${searchParams.toString()}` : '';
      void navigate(`/workspace/${workspaceId}/workflows${query}`, {
        replace: logic === RouteLogic.REPLACE,
      });
    },
    [navigate]
  );

  const jumpToChatThread = useCallback(
    (
      workspaceId: string,
      sessionId: string,
      docId?: string | null,
      logic: RouteLogic = RouteLogic.PUSH
    ) => {
      void navigate(
        `/workspace/${workspaceId}${createChatThreadPath(sessionId, docId)}`,
        { replace: logic === RouteLogic.REPLACE }
      );
    },
    [navigate]
  );

  const jumpToAppBases = useCallback(
    (workspaceId: string, logic: RouteLogic = RouteLogic.PUSH) => {
      void navigate(`/workspace/${workspaceId}/app-bases`, {
        replace: logic === RouteLogic.REPLACE,
      });
    },
    [navigate]
  );

  const jumpToHub = useCallback(
    (
      workspaceId: string,
      params: URLSearchParams,
      logic: RouteLogic = RouteLogic.PUSH
    ) => {
      void navigate(
        `/workspace/${workspaceId}/marketplace?${params.toString()}`,
        {
          replace: logic === RouteLogic.REPLACE,
        }
      );
    },
    [navigate]
  );

  const jumpToIndex = useCallback(
    (logic: RouteLogic = RouteLogic.PUSH, opt?: { search?: string }) => {
      void navigate(
        { pathname: '/', search: opt?.search },
        {
          replace: logic === RouteLogic.REPLACE,
        }
      );
    },
    [navigate]
  );

  const jumpTo404 = useCallback(
    (logic: RouteLogic = RouteLogic.PUSH) => {
      void navigate('/404', {
        replace: logic === RouteLogic.REPLACE,
      });
    },
    [navigate]
  );
  const jumpToExpired = useCallback(
    (logic: RouteLogic = RouteLogic.PUSH) => {
      void navigate('/expired', {
        replace: logic === RouteLogic.REPLACE,
      });
    },
    [navigate]
  );
  const jumpToSignIn = useCallback(
    (
      redirectUri?: string,
      logic: RouteLogic = RouteLogic.PUSH,
      otherOptions?: Omit<NavigateOptions, 'replace'>,
      params?: Record<string, string>
    ) => {
      const searchParams = new URLSearchParams();

      if (redirectUri) {
        searchParams.set('redirect_uri', redirectUri);
      }

      if (params) {
        for (const key in params) searchParams.set(key, params[key]);
      }

      void navigate(
        '/sign-in' +
          (searchParams.toString() ? '?' + searchParams.toString() : ''),
        {
          replace: logic === RouteLogic.REPLACE,
          ...otherOptions,
        }
      );
    },
    [navigate]
  );

  const jumpToOpenInApp = useCallback(
    (url: string, newTab = true) => {
      const deeplink = getOpenUrlInDesktopAppLink(url, newTab);

      if (!deeplink) {
        return;
      }

      const encodedUrl = encodeURIComponent(deeplink);
      void navigate(`/open-app/url?url=${encodedUrl}`);
    },
    [navigate]
  );

  const jumpToImportTemplate = useCallback(
    (name: string, snapshotUrl: string) => {
      void navigate(
        `/template/import?name=${encodeURIComponent(name)}&snapshotUrl=${encodeURIComponent(snapshotUrl)}`
      );
    },
    [navigate]
  );

  const jumpToWorkspaceSettings = useCallback(
    (
      workspaceId: string,
      tab?: SettingTab,
      request: { anchor?: string; subjectId?: string } = {},
      logic: RouteLogic = RouteLogic.PUSH
    ) => {
      const searchParams = new URLSearchParams();
      if (tab) {
        searchParams.set('tab', tab);
      }
      if (request.anchor) searchParams.set('anchor', request.anchor);
      if (request.subjectId) searchParams.set('subject', request.subjectId);
      void navigate(
        `/workspace/${workspaceId}/settings?${searchParams.toString()}`,
        {
          replace: logic === RouteLogic.REPLACE,
        }
      );
    },
    [navigate]
  );
  return useMemo(
    () => ({
      jumpToPage,
      jumpToPageBlock,
      jumpToPageComment,
      jumpToIndex,
      jumpTo404,
      openPage,
      jumpToWorkflows,
      jumpToAppBases,
      jumpToHub,
      jumpToChatThread,
      jumpToExpired,
      jumpToSignIn,
      jumpToCollection,
      jumpToAll,
      jumpToCollections,
      jumpToTags,
      jumpToTag,
      jumpToOpenInApp,
      jumpToImportTemplate,
      jumpToWorkspaceSettings,
    }),
    [
      jumpToPage,
      jumpToPageBlock,
      jumpToPageComment,
      jumpToIndex,
      jumpTo404,
      openPage,
      jumpToWorkflows,
      jumpToAppBases,
      jumpToHub,
      jumpToChatThread,
      jumpToExpired,
      jumpToSignIn,
      jumpToCollection,
      jumpToAll,
      jumpToCollections,
      jumpToTags,
      jumpToTag,
      jumpToOpenInApp,
      jumpToImportTemplate,
      jumpToWorkspaceSettings,
    ]
  );
}
